<template>
  <div class="row mt-3">
    <div class="col-12 pt-3">
      <SectionTitle has-line content="Consulta de Pagos Retroactivos" />
    </div>

    <div class="col-12">
      <ValidationProvider
        vid="hasRetroactivePaymentAuthorization"
        name="Autorización de pagos retroactivos"
        v-slot="{ errors }"
        :rules="needToValidate ? 'required' : ''"
        :custom-messages="{
          required:
            'Debe responder si la consulta tiene autorizados pagos retroactivos para continuar.',
        }"
      >
        <custom-radio
          v-model="model.hasRetroactivePaymentAuthorization"
          name="hasRetroactivePaymentAuthorization"
          class="w-75"
          is-inline
          label="¿La consulta tiene autorizados pagos retroactivos?"
          :is-disabled="readonly"
          :options="yesNoOptions"
          :error="Boolean(errors[0])"
          custom-error-msg="Favor marque sí o no."
        />

        <div v-if="errors[0]" class="text-danger mt-1">
          {{ errors[0] }}
        </div>
      </ValidationProvider>
    </div>

    <div
      v-if="model.hasRetroactivePaymentAuthorization === false"
      class="col-12 m-2"
    >
      <div class="alert alert-warning" role="alert">
        <div class="alert-content">
          <i class="fas mr-2 fa-bell-exclamation" />
          <span>
            Los meses ingresados sobrepasan la fecha límite del año escolar. No
            es posible avanzar sin autorización de pagos retroactivos.
          </span>
        </div>
      </div>
    </div>

    <template v-if="model.hasRetroactivePaymentAuthorization === true">
      <div class="col-12">
        <ValidationProvider
          vid="retroactivePaymentJustification"
          name="Justificación de Pago Retroactivo"
          v-slot="{ errors }"
          :rules="
            needToValidate && model.hasRetroactivePaymentAuthorization === true
              ? 'required'
              : ''
          "
          :custom-messages="{
            required:
              'Debe ingresar la justificación del pago retroactivo para continuar.',
          }"
        >
          <custom-text-area
            v-model="model.retroactivePaymentJustification"
            label="Justificación de Pago Retroactivo:"
            :maxlength="3000"
            class="w-100 pl-2"
            :show-char-count="true"
            :disabled="readonly"
            :error="errors[0]"
            :custom-error-msg="errors[0] || ''"
          />
        </ValidationProvider>
      </div>
      <div class="col-12 mt-3">
        <ValidationProvider
          vid="retroactivePaymentDocuments"
          name="Documentos que sustentan pago retroactivo"
          v-slot="{ errors }"
          :rules="retroactiveDocumentsValidationRules"
          :custom-messages="{
            required: retroactiveDocumentsRequiredMessage,
          }"
        >
          <input type="hidden" :value="retroactiveDocumentsValidationValue" />

          <Documents
            :disabled="readonly"
            :documents="model.retroactivePaymentDocuments"
            :document-types="documentTypes"
            :separate-amendment-documents="false"
            center-title-header="Documentos que sustentan pago retroactivo"
            @files="onFiles"
          />

          <div v-if="errors[0]" class="custom-error-msg mt-1">
            {{ errors[0] }}
          </div>
        </ValidationProvider>
      </div>
    </template>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomRadio from 'custom-radio';
import CustomTextArea from 'CustomTextArea';
import { Documents } from '@/components/pei/ubication-tech-assistance/_components';
import { ValidationProvider } from 'vee-validate';
import Error from 'custom-error';
import documentTypes from '@/utils/constants/documentTypes';

export default {
  name: 'RetroactivePayments',
  components: {
    SectionTitle,
    CustomRadio,
    CustomTextArea,
    Documents,
    ValidationProvider,
    Error,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    readonly: { type: Boolean, default: false },
    needToValidate: { type: Boolean, default: false },
    documentTypes: { type: Array, default: () => [] },
    isComplaintRequest: { type: Boolean, default: false },
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    hasRetroactiveDocuments() {
      return !!this.model.retroactivePaymentDocuments?.files?.length;
    },

    retroactiveDocumentTypeIds() {
      const documents = this.model.retroactivePaymentDocuments || {};
      const typeIds = documents.types || [];
      const fileTypeIds = (documents.files || []).map(
        (file) => file.documentTypeId || file.documentType
      );

      return [...typeIds, ...fileTypeIds].map((typeId) => Number(typeId));
    },

    yesNoOptions() {
      return [
        {
          value: true,
          label: 'Sí',
          check: this.model.hasRetroactivePaymentAuthorization === true,
        },
        {
          value: false,
          label: 'No',
          check: this.model.hasRetroactivePaymentAuthorization === false,
        },
      ];
    },

    hasComplaintCopyDocument() {
      return this.retroactiveDocumentTypeIds.includes(
        documentTypes.CopiaDeQuerella
      );
    },

    retroactiveDocumentsValidationRules() {
      if (
        !this.needToValidate ||
        this.model.hasRetroactivePaymentAuthorization !== true
      ) {
        return '';
      }

      return {
        required: true,
      };
    },

    retroactiveDocumentsValidationValue() {
      if (this.isComplaintRequest) {
        return this.hasComplaintCopyDocument ? '1' : '';
      }

      return this.hasRetroactiveDocuments ? '1' : '';
    },

    retroactiveDocumentsRequiredMessage() {
      if (this.isComplaintRequest) {
        return 'Debe adjuntar una Copia de Querella para continuar.';
      }

      return 'Debe adjuntar al menos un documento que sustente el pago retroactivo para continuar.';
    },
  },
  methods: {
    onFiles(data) {
      this.model = {
        ...this.model,
        retroactivePaymentDocuments: data,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-content {
  color: #af6708;
}
.custom-error-msg {
  text-align: left;
  font: Lato, Regular;
  letter-spacing: 0;
  color: #ff5b5c;
  margin-bottom: 0;
  font-weight: 400;
}
</style>
